import { inject, Injectable, signal, computed } from '@angular/core';
import { Database, ref, get, onValue } from '@angular/fire/database';
import { HttpClient } from '@angular/common/http';
import { CurrencyService } from '../../shared/services/currency.service';
import { LanguageService } from '../../shared/services/language.service';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class UnitService {
  private db = inject(Database);
  private http = inject(HttpClient);
  private _unitInfo = signal<any>(null);
  private _currentRouteName = signal<string>('');
  private _socialMediaLinks = signal<any>(null);
  private _clientDisplayTimeZone = signal<string>('Europe/Stockholm');
  readonly socialMedia = computed(() => this._socialMediaLinks())
  readonly unitInfo = computed(() => this._unitInfo());
  readonly routeName = computed(() => this._currentRouteName());
  private currencyProvider = inject(CurrencyService);
  private languageService = inject(LanguageService);
  private _unitConfigurations = signal<any>(null);
  readonly unitConfigrations = computed(() => this._unitConfigurations());
  private _articleAccountings = signal<Record<string, { tax?: number }>>({});
  readonly articleAccountings = computed(() => this._articleAccountings());
  private _paymentProvider = signal<string>('stripe');
  readonly paymentProvider = computed(() => this._paymentProvider());
  private _cardEnabled = signal<boolean>(false);
  readonly cardEnabled = computed(() => this._cardEnabled());
  private _mobilepayEnabled = signal<boolean>(false);
  readonly mobilepayEnabled = computed(() => this._mobilepayEnabled());
  /** Apple Pay / Google Pay ride on top of card payments, available for both Stripe and Reepay. */
  private readonly _cardWalletEnabled = computed(() =>
    this._cardEnabled() &&
    (this._paymentProvider() === 'reepay' || this._paymentProvider() === 'stripe')
  );

  readonly applepayEnabled = this._cardWalletEnabled;
  readonly googlepayEnabled = this._cardWalletEnabled;
  private _swishPaymentEnabled = signal<boolean>(false);
  readonly swishPaymentEnabled = computed(() => this._swishPaymentEnabled());
  private _servingMethods = signal<{
    deliveryEnabled: boolean;
    pickupEnabled: boolean;
    guestInvoiceEnabled: boolean;
  }>({
    deliveryEnabled: true,
    pickupEnabled: true,
    guestInvoiceEnabled: false,
  });
  readonly servingMethods = computed(() => this._servingMethods());
  readonly deliveryServingEnabled = computed(() => this._servingMethods().deliveryEnabled);
  /**
   * Whether ANY delivery-shaped order can be placed: corporate delivery, or guest
   * invoice (which is a delivery order without login). Use this — not
   * deliveryServingEnabled — wherever the question is "is the delivery schedule
   * open", so a guest-invoice-only unit isn't shown as closed. Real closed days
   * still come from the delivery availability/deviations themselves.
   */
  readonly deliveryOrderingEnabled = computed(
    () => this._servingMethods().deliveryEnabled || this._servingMethods().guestInvoiceEnabled
  );
  readonly pickupServingEnabled = computed(() => this._servingMethods().pickupEnabled);
  /**
   * Guest "order by invoice" checkout: no login, no corporate customer record,
   * company/billing details typed in at checkout. Opt-in per unit, defaults off
   * so nothing changes for units that have not enabled it.
   */
  readonly guestInvoiceEnabled = computed(() => this._servingMethods().guestInvoiceEnabled);
  /**
   * B2B display: show prices excluding VAT. Display only — what is charged and
   * sent with the order stays the stored gross price. Opt-in per unit, defaults off.
   */
  private _showPricesExVat = signal<boolean>(false);
  readonly showPricesExVat = computed(() => this._showPricesExVat());
  readonly clientDisplayTimeZone = computed(() => this._clientDisplayTimeZone());
  constructor() { }

  private applyRouteNameTimeZoneFallback(routeName: string): void {
    const r = (routeName || '').toLowerCase();
    if (r.startsWith('compassfi')) this._clientDisplayTimeZone.set('Europe/Helsinki');
    else if (r.startsWith('compassdk')) this._clientDisplayTimeZone.set('Europe/Copenhagen');
    else if (r.startsWith('compassno')) this._clientDisplayTimeZone.set('Europe/Oslo');
    else this._clientDisplayTimeZone.set('Europe/Stockholm');
  }

  async resolveClientDisplayTimeZone(routeName: string): Promise<void> {
    if (!routeName) return;
    const url = `${environment.ApiUrl}/settings/v1/getResolvedClientTimeZone`;
    try {
      const tz = await firstValueFrom(
        this.http.post(url, { routeName }, { responseType: 'text' })
      );
      const value = (tz || '').trim();
      if (value) {
        this._clientDisplayTimeZone.set(value);
      } else {
        this.applyRouteNameTimeZoneFallback(routeName);
      }
    } catch {
      this.applyRouteNameTimeZoneFallback(routeName);
    }
  }

  async loadUnitInfo(routeName: string): Promise<void> {
    this._currentRouteName.set(routeName);

    // Register realtime listeners up-front so their values stream in immediately
    // and in parallel, instead of only starting after the awaited reads below.
    this.registerRealtimeListeners(routeName);

    try {
      const [, , unitInfo, socialMediaLinks] = await Promise.all([
        this.resolveClientDisplayTimeZone(routeName),
        this.languageService.initializeDefaultLanguage(routeName),
        this.fetchPublicInfo(routeName),
        this.fetchSocialMediaLinks(routeName),
      ]);

      this._unitInfo.set(unitInfo);
      this._socialMediaLinks.set(socialMediaLinks);
    } catch (error) {
      this._unitInfo.set(null);
    }
  }

  private async fetchPublicInfo(routeName: string): Promise<any> {
    const snapshot = await get(
      ref(this.db, `adminApp/clients/${routeName}/info/public`)
    );
    return snapshot.val();
  }

  private async fetchSocialMediaLinks(routeName: string): Promise<any> {
    const snapshot = await get(
      ref(this.db, `Clients/${routeName}/AppInfo/style`)
    );
    return snapshot.val();
  }

  /** Detach handles for the listeners registered by registerRealtimeListeners(). */
  private realtimeUnsubscribers: Array<() => void> = [];
  /** Unit these listeners belong to, so re-entering the same unit is a no-op. */
  private listenersRouteName: string | null = null;

  /**
   * Detaches the live config listeners. This service is providedIn:'root', so
   * without this every visit to a unit route left its listeners attached.
   */
  private teardownRealtimeListeners(): void {
    for (const unsubscribe of this.realtimeUnsubscribers) {
      try {
        unsubscribe();
      } catch {
        // A listener that is already gone must not block the rest.
      }
    }
    this.realtimeUnsubscribers = [];
    this.listenersRouteName = null;
  }

  /**
   * Subscribes to the live config nodes that back currency, serving methods, VAT
   * and payments.
   *
   * unitResolver calls loadUnitInfo() on every navigation to /u/:routeName, so
   * this runs again each time a unit is opened. Previous listeners are detached
   * first: they used to accumulate (six per visit), and because each callback
   * writes a signal, the redundant callbacks multiplied change-detection work
   * on every config change until the page locked up.
   */
  private registerRealtimeListeners(routeName: string): void {
    if (this.listenersRouteName === routeName && this.realtimeUnsubscribers.length) {
      return;
    }
    this.teardownRealtimeListeners();
    this.listenersRouteName = routeName;

    const currencyRef = ref(this.db, `Clients/${routeName}/AppInfo/Context/currencyToUse`);
    this.realtimeUnsubscribers.push(onValue(currencyRef, (snapshot) => {
      const currency = snapshot.val();
      this.currencyProvider.setCurrency(currency);
    }));

    const unitConfigurationsRef = ref(this.db, `/Clients/${routeName}/catering/configuration/text`);
    this.realtimeUnsubscribers.push(onValue(unitConfigurationsRef, (snapshot) => {
      const unitConfigurations = snapshot.val();
      this._unitConfigurations.set(unitConfigurations);
    }));

    const servingMethodsRef = ref(this.db, `/Clients/${routeName}/catering/configuration/servingMethods`);
    this.realtimeUnsubscribers.push(onValue(servingMethodsRef, (snapshot) => {
      const servingMethods = snapshot.val();
      this._servingMethods.set({
        deliveryEnabled: typeof servingMethods?.deliveryEnabled === 'boolean' ? servingMethods.deliveryEnabled : true,
        pickupEnabled: typeof servingMethods?.pickupEnabled === 'boolean' ? servingMethods.pickupEnabled : true,
        guestInvoiceEnabled: servingMethods?.guestInvoiceEnabled === true,
      });
    }));

    const showPricesExVatRef = ref(this.db, `/Clients/${routeName}/catering/configuration/showPricesExVat`);
    this.realtimeUnsubscribers.push(onValue(showPricesExVatRef, (snapshot) => {
      this._showPricesExVat.set(snapshot.val() === true);
    }));

    const articleAccountingsRef = ref(this.db, `adminApp/clients/${routeName}/settings/vat/articleAccountings`);
    this.realtimeUnsubscribers.push(onValue(articleAccountingsRef, (snapshot) => {
      this._articleAccountings.set(snapshot.val() || {});
    }));

    const paymentProviderRef = ref(this.db, `Clients/${routeName}/AppInfo/Context/paymentProvider`);
    this.realtimeUnsubscribers.push(onValue(paymentProviderRef, (snapshot) => {
      const paymentProvider = snapshot.val();
      if (paymentProvider && typeof paymentProvider === 'string') {
        const normalizedProvider = paymentProvider.toLowerCase().trim();
        if (normalizedProvider === 'stripe' || normalizedProvider === 'reepay') {
          this._paymentProvider.set(normalizedProvider);
        } else {
          this._paymentProvider.set('stripe');
        }
      } else {
        this._paymentProvider.set('stripe');
      }
    }));

    const paymentOptionsRef = ref(this.db, `Clients/${routeName}/AppInfo/Context/paymentOptions`);
    this.realtimeUnsubscribers.push(onValue(paymentOptionsRef, (snapshot) => {
      const paymentOptions = snapshot.val();
      if (paymentOptions?.card?.enabled !== undefined) {
        this._cardEnabled.set(paymentOptions.card.enabled === true);
      }
      if (paymentOptions?.mobilepay?.enabled !== undefined) {
        this._mobilepayEnabled.set(paymentOptions.mobilepay.enabled === true);
      }
      if (paymentOptions?.swish?.enabled !== undefined) {
        this._swishPaymentEnabled.set(paymentOptions.swish.enabled === true);
      }
    }));
  }
}
